<template>
  <div
    class="px-2"
    style="min-height: 100%; max-width: 600px"
  >
    <div class="form-group pb-2">
      <label for="paymentTitle">{{ $t('payment.title') }}</label>
      <input
        id="paymentTitle"
        v-model="title"
        class="form-control"
        data-test="payment-title"
        :placeholder="$t('payment.title')"
      >
    </div>
    <div class="row no-gutters mx-0 w-100 pb-2">
      <div class="col-6">
        <div class="form-group mr-2">
          <label for="paymentTitle">{{ $t('bill.code') }}</label>
          <input
            v-model="code"
            class="form-control"
            :placeholder="$t('bill.code')"
          >
        </div>
      </div>
      <div
        v-if="payee.categories.length > 1"
        class="col-6"
      >
        <div class="ml-2">
          <label>{{ $t('bill.category') }}</label>
          <CategoryDropdown
            :selected.sync="category"
            :invalid="invalidCategoryAccountNumber"
          />
        </div>
      </div>
    </div>

    <div class="row no-gutters mx-0 w-100 pb-2">
      <div class="col-6">
        <div class="form-group mr-2">
          <label for="paymentTitle">{{ $t('bill.vatCode') }}</label>
          <BFormSelect
            v-model="vatCode"
            :options="vatCodeOptions"
          />
        </div>
      </div>
      <div
        v-if="!hideBankAccount"
        class="col-6"
      >
        <div class="form-group ml-2">
          <label>{{ $t('payment.accountNumber') }}</label>
          <BankAccountSelect
            :bank-account-id.sync="bankAccountId"
            :validation="null"
            :find-default-bank-account="false"
          />
        </div>
      </div>
    </div>

    <div
      v-if="invalidCategoryAccountNumber"
      class="alert alert-danger"
    >
      {{ $t('bill.invalidCategoryAccountNumber', [formatAccountNumber(categoryAccountNumber)]) }}
    </div>
    <div class="form-group pb-3">
      <label for="paymentDesc">{{ $t('payment.description') }}</label>
      <textarea
        id="paymentDesc"
        v-model="description"
        class="form-control"
        data-test="payment-description"
        :placeholder="$t('payment.description')"
      />
    </div>
    <div class="row no-gutters">
      <div
        v-if="type !== 'recurring'"
        class="col-6 pb-3"
      >
        <div class="mr-2">
          <label>{{ $t('payment.startDate') }}</label>
          <CtkDateTimePicker
            id="repeat-from"
            v-model="from"
            format="YYYY-MM-DD"
            formatted="ddd D MMM YYYY"
            :label="$t('payment.repeatFrom')"
            :locale="locale"
            :only-date="true"
            data-test="bills-payment-from"
          />
        </div>
      </div>
      <div class="col-6 pb-3">
        <div class="ml-2">
          <label>{{ $t('payment.daysToPay') }}</label>
          <input
            v-model="paymentTime"
            type="number"
            class="form-control"
            value="14"
            data-test="days-for-payment"
          >
        </div>
      </div>
    </div>

    <hr v-if="!hideType">

    <div
      v-if="!hideType"
      class="d-flex pt-3"
    >
      <div
        style="width: 200px"
        class="mx-2"
      >
        <button
          class="btn btn-block"
          :class="!isPlan ? 'btn-primary' : 'btn-outline-primary'"
          @click="isPlan = false"
        >
          <i class="icon-bill pr-1" />
          {{ $t('payment.single') }}
        </button>
      </div>
      <div
        style="width: 200px"
        class="mx-2"
      >
        <button
          class="btn btn-block"
          :class="isPlan ? 'btn-primary' : 'btn-outline-primary'"
          @click="
            isPlan = true;
            scrollToRecurring();
          "
        >
          <i class="fas fa-calendar pr-1" />
          {{ $t('payment.recurring') }}
        </button>
      </div>
    </div>

    <div id="recurring-settings">
      <div v-if="isPlan">
        <div class="form-group mb-2 mt-4">
          <div class="row no-gutters mx-0">
            <div class="col-6 pr-1">
              <label for="recurringEvery">{{ $t('payment.repeatEvery') }}</label>
              <input
                id="recurringEveryNum"
                v-model="repeatNumber"
                required
                type="number"
                min="1"
                max="30"
                class="form-control"
                data-test="recurring-repeat-every-number"
              >
            </div>
            <div class="col-6 pl-1">
              <label class="text-white">.&zwnj;</label>
              <select
                v-model="repeatType"
                class="custom-select"
                data-test="recurring-repeat-every-type"
              >
                <option value="Month">
                  {{ $t(`time.months.${getPluralType(repeatNumber)}`) }}
                </option>
                <option value="Week">
                  {{ $t(`time.weeks.${getPluralType(repeatNumber)}`) }}
                </option>
                <option value="Day">
                  {{ repeatNumber > 1 ? $t('time.days') : $t('time.day') }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div class="row no-gutters mx-0">
          <div class="col-12 form-group mb-2 mt-4">
            <div class="row justify-content-center">
              <div class="col-6">
                <label>{{ $t('payment.repeatFrom') }}</label>
                <CtkDateTimePicker
                  id="repeat-from"
                  v-model="from"
                  format="YYYY-MM-DD"
                  formatted="ddd D MMM YYYY"
                  :label="$t('payment.repeatFrom')"
                  :locale="locale"
                  :only-date="true"
                  data-test="bills-payment-from"
                />
              </div>
              <div class="col-6">
                <label>{{ $t('payment.repeatTo') }}</label>
                <CtkDateTimePicker
                  id="repeat-to"
                  v-model="to"
                  format="YYYY-MM-DD"
                  formatted="ddd D MMM YYYY"
                  :label="$t('payment.repeatTo')"
                  :locale="locale"
                  :only-date="true"
                  data-test="bills-payment-to"
                />
              </div>
            </div>
            <div class="recurring-description">
              <i class="ion ion-ios-information-circle-outline" />
              <RepeatInfo
                :repeat-type="repeatType"
                :repeat-number="repeatNumber"
                :from="from"
              />
            </div>
          </div>
        </div>
        <div class="row no-gutters">
          <div class="col-6 pr-1">
            <label for="earlyExecutionDays">{{ $t('billingPlan.generateDaysBefore') }}</label>
            <input
              id="earlyExecutionDays"
              v-model="earlyExecutionDays"
              required
              type="number"
              min="0"
              :max="maxEarlyExecutionDays"
              step="1"
              class="form-control"
              :class="{ 'is-invalid': !earlyExecutionDaysValid }"
              data-test="recurring-early-execution-days"
            >
            <div
              v-if="!earlyExecutionDaysValid"
              class="invalid-feedback d-block"
            >
              {{ $t('billingPlan.generateDaysBeforeInvalid', { max: maxEarlyExecutionDays }) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { accountNumber } from '@/utils/filters';
import moment from 'moment';
import CtkDateTimePicker from 'vue-ctk-date-time-picker';
import { mapGetters } from 'vuex';
import { vatCodeOptions } from '@/utils/vat';
import BankAccountSelect from '@/components/bills/form/BankAccountSelect';
import CategoryDropdown from './CategoryDropdown';
import RepeatInfo from './RepeatInfo';

const noVatCode = 'Unknown';

export default {
  props: {
    hideType: Boolean,
    hideBankAccount: Boolean,
    billIsPlan: Boolean,
    billTitle: String,
    billCode: String,
    billDescription: String,
    billPaymentTime: [String, Number],
    billRepeatNumber: [String, Number],
    billRepeatType: String,
    billFrom: String,
    billTo: String,
    billBankAccountId: String,
    billCategory: String,
    billEarlyExecutionDays: [String, Number],
    billRuleValid: Boolean,
    billVatCode: String,
    accountNumber: String,
  },
  data: () => ({
    title: '',
    description: '',
    code: '',
    isPlan: false,
    repeatType: 'Month',
    paymentTime: '14',
    repeatNumber: '1',
    vatCode: '',
    bankAccountId: '',
    category: '',
    earlyExecutionDays: 1,
    from: moment.utc().format('YYYY-MM-DD'),
    to: null,
    prefillFromCategory: true,
    vatCodeOptions,
  }),
  components: {
    RepeatInfo,
    BankAccountSelect,
    CategoryDropdown,
    CtkDateTimePicker,
  },
  computed: {
    ...mapGetters(['locale', 'payee']),
    defaultCategory() {
      return this.payee?.categories.find((x) => x.isDefault);
    },
    selectedCategory() {
      return this.payee?.categories.find((x) => x.id === this.category);
    },
    categoryVatCode() {
      const vatCode = this.selectedCategory?.defaultVatCode;
      return vatCode && vatCode !== noVatCode ? vatCode : null;
    },
    categoryAccountingCode() {
      return this.selectedCategory?.defaultAccountingCode || '';
    },
    invalidCategoryAccountNumber() {
      return this.categoryAccountNumber && this.accountNumber && this.accountNumber !== this.categoryAccountNumber;
    },
    categoryAccountNumber() {
      const gateway = this.payee.payGateways?.find((x) => x.categoryIds.includes(this.category))
          || this.payee.payGateways?.find((x) => x.isDefault);

      return gateway?.accountNumber;
    },
    // shortest possible billing period in days - mirrors TimeRule.MinPeriodDays on the API side
    minPeriodDays() {
      const count = Math.max(1, parseInt(this.repeatNumber, 10) || 1);
      switch (this.repeatType) {
        case 'Day': return count;
        case 'Week': return 7 * count;
        case 'Month': return 28 * count;
        case 'Quater': return 90 * count;
        case 'Year': return 365 * count;
        default: return count;
      }
    },
    // a bill may be generated ahead of time, but never a whole billing period ahead (API caps at 60 days)
    maxEarlyExecutionDays() {
      return Math.min(60, this.minPeriodDays - 1);
    },
    earlyExecutionDaysValid() {
      const days = Number(this.earlyExecutionDays);
      return this.earlyExecutionDays !== '' && this.earlyExecutionDays !== null
        && Number.isInteger(days) && days >= 0 && days <= this.maxEarlyExecutionDays;
    },
    repeatNumberValid() {
      const count = Number(this.repeatNumber);
      return Number.isInteger(count) && count >= 1;
    },
    ruleValid() {
      return !this.isPlan || (this.earlyExecutionDaysValid && this.repeatNumberValid);
    },
  },
  watch: {
    title(t) {
      this.$emit('update:billTitle', t);
    },
    code(t) {
      this.$emit('update:billCode', t);
    },
    description(d) {
      this.$emit('update:billDescription', d);
    },
    repeatNumber(d) {
      this.$emit('update:billRepeatNumber', d);
    },
    repeatType(d) {
      this.$emit('update:billRepeatType', d);
    },
    paymentTime(d) {
      this.$emit('update:billPaymentTime', d);
    },
    from(d) {
      this.$emit('update:billFrom', d);
    },
    to(d) {
      this.$emit('update:billTo', d);
    },
    category(d) {
      this.$emit('update:billCategory', d);
      if (this.prefillFromCategory) {
        this.vatCode = this.categoryVatCode;
        this.code = this.categoryAccountingCode;
      }
      this.prefillFromCategory = true;
    },
    isPlan(d) {
      this.$emit('update:billIsPlan', d);
    },
    earlyExecutionDays(d) {
      this.$emit('update:billEarlyExecutionDays', d);
    },
    ruleValid: {
      immediate: true,
      handler(v) {
        this.$emit('update:billRuleValid', v);
      },
    },
    vatCode(d) {
      this.$emit('update:billVatCode', d);
    },
    bankAccountId(d) {
      if (!this.hideBankAccount) {
        this.$emit('update:billBankAccountId', d);
      }
    },
  },
  methods: {
    formatAccountNumber: accountNumber,
    getPluralType(m) {
      const n = parseInt(m, 10);
      if (Number.isNaN(n)) return 'zero';
      if (n === 0) return 'zero';
      if (n === 1) return 'one';
      if (n < 5) return 'few';
      return 'many';
    },
    scrollToRecurring() {
      this.$scrollTo('#recurring-settings', 300, {
        container: '#title-and-description',
        offset: 0,
      });
    },
  },
  created() {
    this.type = this.billType;
    this.category = this.billCategory;
    this.description = this.billDescription;
    this.title = this.billTitle;
    this.code = this.billCode;
    this.repeatNumber = this.billRepeatNumber;
    this.repeatType = this.billRepeatType;
    this.paymentTime = this.billPaymentTime;
    this.isPlan = this.billIsPlan;
    this.from = this.billFrom;
    this.to = this.billTo;
    this.vatCode = this.billVatCode;
    this.bankAccountId = this.billBankAccountId;
    this.earlyExecutionDays = this.billEarlyExecutionDays;

    if (!this.category && this.defaultCategory) {
      this.category = this.defaultCategory.id;
    }

    if (!this.vatCode) {
      this.vatCode = this.categoryVatCode;
    }

    if (!this.code) {
      this.code = this.categoryAccountingCode;
    }

    // the category watcher fires once for the value set above - the bill's own values win there
    this.prefillFromCategory = this.category === '';
  },
};
</script>

<style lang="scss" scoped>
  .recurring-description {
    color: #858383;
    font-size: 13px;
    padding-top: 5px;
  }
</style>
